import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Tabs, Tab, Grid, 
  Chip, Alert, CircularProgress, Divider,
  List, ListItem, ListItemText, ListItemIcon, ListItemSecondaryAction,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem, Switch,
  Tooltip, Badge, Button
} from '@mui/material';
import {
  Security as SecurityIcon,
  Videocam as CameraIcon,
  People as PeopleIcon,
  DoorFront as DoorIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Lock as LockIcon,
  LockOpen as UnlockIcon,
  Visibility as ViewIcon,
  VisibilityOff as HideIcon,
  CheckCircle as OnlineIcon,
  Error as OfflineIcon,
  Warning as WarningIcon,
  Settings as SetupIcon,
  Dashboard as DashboardIcon
} from '@mui/icons-material';
import ModernLayout from '../common/ModernLayout';
import ModernCard, { StatsCard } from '../common/ModernCard';
import ModernButton, { GradientButton, GlassButton } from '../common/ModernButton';
import AccessControl from './AccessControl';
import CameraManagement from './CameraManagement';
import EmployeeIntegration from './EmployeeIntegration';
import UniFiSettings from './UniFiSettings';
import UniFiSetupWizard from './UniFiSetupWizard';
import unifiAPIService from './UniFiAPIService';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';

const UniFiControl = () => {
  const { translate } = useLanguage();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState('checking');
  const [unifiStatus, setUnifiStatus] = useState({
    access: { online: false, devices: 0 },
    protect: { online: false, cameras: 0 },
    network: { online: false, devices: 0 }
  });
  const [showSetupWizard, setShowSetupWizard] = useState(false);
  const [isConfigured, setIsConfigured] = useState(false);

  useEffect(() => {
    initializeUniFi();
  }, []);

  const initializeUniFi = async () => {
    // Load configuration from localStorage
    const configLoaded = unifiAPIService.loadConfig();
    setIsConfigured(configLoaded);

    if (configLoaded) {
      await checkUniFiConnection();
      await fetchUniFiStatus();
    } else {
      setConnectionStatus('not-configured');
    }
  };

  const checkUniFiConnection = async () => {
    try {
      setConnectionStatus('checking');
      const result = await unifiAPIService.testConnection();
      setConnectionStatus(result.success ? 'connected' : 'disconnected');
    } catch (error) {
      console.error('Error checking UniFi connection:', error);
      setConnectionStatus('error');
    }
  };

  const fetchUniFiStatus = async () => {
    try {
      const [networkResult, accessResult, protectResult] = await Promise.all([
        unifiAPIService.getDevices(),
        unifiAPIService.getAccessDevices(),
        unifiAPIService.getProtectCameras()
      ]);

      setUnifiStatus({
        network: {
          online: networkResult.success,
          devices: networkResult.success ? networkResult.data.length : 0
        },
        access: {
          online: accessResult.success,
          devices: accessResult.success ? accessResult.data.length : 0
        },
        protect: {
          online: protectResult.success,
          cameras: protectResult.success ? protectResult.data.length : 0
        }
      });
    } catch (error) {
      console.error('Error fetching UniFi status:', error);
    }
  };

  const handleSetupComplete = async (config) => {
    unifiAPIService.saveConfig(config);
    setIsConfigured(true);
    setShowSetupWizard(false);
    await initializeUniFi();
  };

  const handleSetupCancel = () => {
    setShowSetupWizard(false);
  };

  const getConnectionStatusColor = () => {
    switch (connectionStatus) {
      case 'connected': return 'success';
      case 'disconnected': return 'error';
      case 'error': return 'warning';
      case 'not-configured': return 'info';
      default: return 'info';
    }
  };

  const getConnectionStatusText = () => {
    switch (connectionStatus) {
      case 'connected': return translate('unifiControl.connected');
      case 'disconnected': return translate('unifiControl.disconnected');
      case 'error': return translate('unifiControl.connectionError');
      case 'not-configured': return translate('unifiControl.notConfigured');
      case 'checking': return translate('unifiControl.checking');
      default: return translate('unifiControl.unknown');
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const renderTabContent = () => {
    if (!isConfigured) {
      return (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <SettingsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" sx={{ mb: 2 }}>
            {translate('unifiControl.setupRequired')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            {translate('unifiControl.setupDescription')}
          </Typography>
          <GradientButton
            startIcon={<SetupIcon />}
            onClick={() => setShowSetupWizard(true)}
            size="large"
          >
            {translate('unifiControl.startSetup')}
          </GradientButton>
        </Box>
      );
    }

    switch (activeTab) {
      case 0:
        return <AccessControl />;
      case 1:
        return <CameraManagement />;
      case 2:
        return <EmployeeIntegration />;
      case 3:
        return <UniFiSettings />;
      default:
        return <AccessControl />;
    }
  };

  const tabLabels = [
    { label: translate('unifiControl.accessControl'), icon: <SecurityIcon /> },
    { label: translate('unifiControl.cameraManagement'), icon: <CameraIcon /> },
    { label: translate('unifiControl.employeeIntegration'), icon: <PeopleIcon /> },
    { label: translate('unifiControl.settings'), icon: <SettingsIcon /> }
  ];

  return (
    <ModernLayout title={translate('unifiControl.title')}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          {translate('unifiControl.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {translate('unifiControl.subtitle')}
        </Typography>
      </Box>

      {/* Connection Status and Management Button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Alert severity={getConnectionStatusColor()} sx={{ flexGrow: 1, mr: 2 }}>
          {getConnectionStatusText()}
        </Alert>
        {isConfigured && (
          <GradientButton
            startIcon={<DashboardIcon />}
            onClick={() => navigate('/unifi-management')}
            size="large"
          >
            Management Dashboard
          </GradientButton>
        )}
      </Box>

      {/* Connection Status */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Chip
            icon={connectionStatus === 'connected' ? <OnlineIcon /> : <OfflineIcon />}
            label={getConnectionStatusText()}
            color={getConnectionStatusColor()}
            variant="outlined"
            sx={{
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
            }}
          />
          {connectionStatus === 'checking' && <CircularProgress size={20} />}
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {isConfigured && (
            <GlassButton
              startIcon={<RefreshIcon />}
              onClick={fetchUniFiStatus}
              disabled={connectionStatus === 'checking'}
            >
              {translate('unifiControl.refreshStatus')}
            </GlassButton>
          )}
          <GlassButton
            startIcon={<SetupIcon />}
            onClick={() => setShowSetupWizard(true)}
          >
            {translate('unifiControl.setup')}
          </GlassButton>
        </Box>
      </Box>

      {/* Connection Alert */}
      {connectionStatus !== 'connected' && isConfigured && (
        <Alert 
          severity={connectionStatus === 'error' ? 'error' : 'warning'} 
          sx={{ 
            mb: 3, 
            borderRadius: 2,
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
          }}
          action={
            <GlassButton 
              color="inherit" 
              size="small" 
              onClick={checkUniFiConnection}
            >
              {translate('unifiControl.retry')}
            </GlassButton>
          }
        >
          {connectionStatus === 'error' 
            ? translate('unifiControl.connectionErrorMsg')
            : translate('unifiControl.connecting')
          }
        </Alert>
      )}

      {/* Status Overview */}
      {isConfigured && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={4}>
            <StatsCard
              title={translate('unifiControl.accessDevices')}
              value={unifiStatus.access.devices.toString()}
              subtitle={unifiStatus.access.online ? translate('unifiControl.systemOnline') : translate('unifiControl.systemOffline')}
              icon={<SecurityIcon />}
              color={unifiStatus.access.online ? 'success' : 'error'}
              status={unifiStatus.access.online ? 'online' : 'offline'}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatsCard
              title={translate('unifiControl.cameras')}
              value={unifiStatus.protect.cameras.toString()}
              subtitle={unifiStatus.protect.online ? translate('unifiControl.systemOnline') : translate('unifiControl.systemOffline')}
              icon={<CameraIcon />}
              color={unifiStatus.protect.online ? 'success' : 'error'}
              status={unifiStatus.protect.online ? 'online' : 'offline'}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatsCard
              title={translate('unifiControl.networkDevices')}
              value={unifiStatus.network.devices.toString()}
              subtitle={unifiStatus.network.online ? translate('unifiControl.systemOnline') : translate('unifiControl.systemOffline')}
              icon={<SecurityIcon />}
              color={unifiStatus.network.online ? 'success' : 'error'}
              status={unifiStatus.network.online ? 'online' : 'offline'}
            />
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      {isConfigured && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            {tabLabels.map((tab, index) => (
              <Tab
                key={index}
                label={tab.label}
                icon={tab.icon}
                iconPosition="start"
                sx={{
                  minHeight: 64,
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              />
            ))}
          </Tabs>
        </Box>
      )}

      {/* Tab Content */}
      <Box sx={{ mt: 3 }}>
        {renderTabContent()}
      </Box>

      {/* Setup Wizard */}
      {showSetupWizard && (
        <UniFiSetupWizard
          onComplete={handleSetupComplete}
          onCancel={handleSetupCancel}
        />
      )}
    </ModernLayout>
  );
};

export default UniFiControl; 